import React from 'react';
import { Box } from '@mui/material';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, LabelList } from 'recharts';

export interface PendingAtBarChartProps {
  data: Array<{
    name: string; // Ex: "Jan"
    fullDate: string; // Ex: "2025-01-01"
    completed: number;
    pendingClient: number;
    pendingDM: number;
    pendingConsultant: number;
  }>;
  height?: number;
  baseLabels: Array<{
    labelname: string;
    dataKey: string;
    color: string;
  }>;
}

// Custom label inside stacked bars
const PendingLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  if (value > 2) {
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        fontSize="12"
        fill="#fff"
        fontWeight="bold"
        dominantBaseline="middle"
      >
        {`${value}%`}
      </text>
    );
  }
  return null;
};

// Correct X-axis with month + year
const CustomXAxisTick = (props: any) => {
  const { x, y, payload, data } = props;
  const { index } = payload;
  const row = data && data[index];

  let year = '';
  if (row?.fullDate) {
    year = `'${new Date(row.fullDate).getFullYear().toString().slice(-2)}`;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#333" fontWeight={500} fontSize={12}>
        {row?.name || ''}
      </text>
      {year && (
        <text x={0} y={0} dy={32} textAnchor="middle" fill="#333" fontWeight={500} fontSize={12}>
          {year}
        </text>
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload, baseLabels }: any) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;

  return (
    <div
      style={{
        background: 'white',
        padding: '12px 14px',
        borderRadius: 8,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #eee',
        minWidth: 160,
        fontSize: 12,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 10,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 6,
        }}
      >
        {item.name}
        {/* {item.fullDate && (
          <span style={{ color: '#888', marginLeft: 6 }}>
            {`'${new Date(item.fullDate).getFullYear().toString().slice(-4)}`}
          </span>
        )} */}
      </div>

      {baseLabels && baseLabels.length > 0 ? (
        baseLabels.map((label: any, index: number) => {
          const rawKey = `${label.dataKey}Raw`;
          return (
            <div key={label.dataKey} style={{ marginBottom: index === baseLabels.length - 1 ? 0 : 4 }}>
              <span style={{ color: label.color, fontWeight: 600 }}>● {label.labelname}:</span> {item[rawKey] ?? 0}
            </div>
          );
        })
      ) : (
        <></>
      )}
    </div>
  );
};

const PendingAtBarChart: React.FC<PendingAtBarChartProps> = ({ data, height = 280, baseLabels }) => {
  const renderCustomXAxisTick = React.useCallback((props) => <CustomXAxisTick {...props} data={data} />, [data]);

  const isEmpty = !data || data.length === 0;

  return (
    <>
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={isEmpty ? [{ name: '', completed: 0, pendingClient: 0, pendingDM: 0, pendingConsultant: 0 }] : data}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          >
            {isEmpty && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey="name" tick={renderCustomXAxisTick} interval={0} />
            <YAxis tick={{ fill: '#333', fontWeight: 500, fontSize: 14 }} />
            <Tooltip content={<CustomTooltip baseLabels={baseLabels} />} />

            {baseLabels?.map((label: any) => (
              <Bar key={label.labelname} dataKey={label.dataKey} stackId="a" fill={label.color}>
                <LabelList dataKey={label.dataKey} content={PendingLabel} />
              </Bar>
            ))}

            {isEmpty && (
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize={18} fill="#999">
                No data found
              </text>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend */}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
        {baseLabels?.map((label: any) => (
          <LegendDot color={label.color} label={label.labelname} />
        ))}
      </Box>
    </>
  );
};

// Reusable legend item
const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: '50%' }} />
    <span style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{label}</span>
  </Box>
);

export default PendingAtBarChart;
